import React, { useEffect, useMemo, useState } from 'react';
import {
  DollarSign,
  RefreshCw,
  Save,
  ExternalLink,
  AlertTriangle,
  Check,
  X,
  Bell,
  Clock,
  History,
  Info,
} from 'lucide-react';
import ConfirmModal from './ConfirmModal';
import {
  getPricing,
  updatePricing,
  listPendingChanges,
  applyPendingChange,
  dismissPendingChange,
  forceResync,
  listCreditBundles,
  type AgentPricingConfig,
  type AgentPricingPendingChange,
  type AgentPricingPutBody,
  type AgentCreditBundle,
} from '../../services/aiUsageApi';
import { RubyLoader } from '../RubyLoader';

import { RubyGemMark } from '../RubyGemMark';
/**
 * PricingTab — global AI pricing dashboard (spec §8.7)
 *
 * - Credit unit price + min purchase
 * - Per-API cards (Anthropic / Vertex / CSE) with burn-rate slider,
 *   cost inputs, enabled toggle, last-synced timestamp, force-resync
 * - Credit Bundles (read-only from Stripe mirror) with Stripe deep link
 * - 🔔 Pending Pricing Changes at top
 * - Change History from config.changeHistory
 * - Save & Publish uses custom ConfirmModal (no window.confirm)
 */

type Dirty<T> = { original: T | null; draft: T | null };

const PricingTab: React.FC = () => {
  const [config, setConfig] = useState<Dirty<AgentPricingConfig>>({ original: null, draft: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);

  const [pending, setPending] = useState<AgentPricingPendingChange[]>([]);
  const [pendingBusySk, setPendingBusySk] = useState<string | null>(null);
  const [pendingError, setPendingError] = useState<string | null>(null);

  const [bundles, setBundles] = useState<AgentCreditBundle[] | null>(null);
  const [bundlesError, setBundlesError] = useState<string | null>(null);

  const [resyncError, setResyncError] = useState<string | null>(null);
  const [resyncBusy, setResyncBusy] = useState<string | null>(null);
  const [resyncAvailable, setResyncAvailable] = useState<Record<string, boolean>>({
    anthropic: true,
    google_vertex: true,
    google_cse: true,
  });

  const [applyAdjustSk, setApplyAdjustSk] = useState<string | null>(null);

  useEffect(() => {
    loadConfig();
    loadPending();
    loadBundles();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const c = await getPricing();
      setConfig({ original: c, draft: JSON.parse(JSON.stringify(c)) });
    } catch (err: any) {
      setError(err?.message || 'Failed to load pricing config');
    } finally {
      setLoading(false);
    }
  };

  const loadPending = async () => {
    setPendingError(null);
    try {
      const items = await listPendingChanges();
      setPending(items.filter((i) => i.status === 'PENDING_REVIEW' || !i.status));
    } catch (err: any) {
      setPendingError(err?.message || 'Failed to load pending changes');
    }
  };

  const loadBundles = async () => {
    setBundlesError(null);
    try {
      setBundles(await listCreditBundles());
    } catch (err: any) {
      setBundlesError(err?.message || 'Bundles not reachable');
    }
  };

  const isDirty = useMemo(() => {
    if (!config.original || !config.draft) return false;
    return JSON.stringify(config.original) !== JSON.stringify(config.draft);
  }, [config]);

  const setDraft = (updater: (d: AgentPricingConfig) => void) => {
    setConfig((prev) => {
      if (!prev.draft) return prev;
      const next = JSON.parse(JSON.stringify(prev.draft));
      updater(next);
      return { ...prev, draft: next };
    });
  };

  const validateDraft = (): string | null => {
    const d = config.draft;
    if (!d) return 'No draft';
    if (!(d.creditUnitPriceUsd > 0)) return 'Credit unit price must be > 0';
    if (!(Number.isInteger(d.minPurchaseCredits) && d.minPurchaseCredits > 0))
      return 'Min purchase must be a positive integer';
    for (const [key, api] of Object.entries(d.apis || {})) {
      if (!api) continue;
      if (!(api.burnRate >= 0 && api.burnRate <= 50))
        return `${key}: burn rate must be 0–50`;
      if (!Number.isInteger(api.minCreditsPerCall) || api.minCreditsPerCall < 0)
        return `${key}: min credits per call must be ≥ 0`;
    }
    return null;
  };

  const handleSaveClick = () => {
    const v = validateDraft();
    if (v) {
      setSaveError(v);
      return;
    }
    setSaveError(null);
    setShowSaveConfirm(true);
  };

  const handleConfirmSave = async () => {
    if (!config.draft) return;
    setSaving(true);
    setSaveError(null);
    try {
      const body: AgentPricingPutBody = {
        creditUnitPriceUsd: config.draft.creditUnitPriceUsd,
        minPurchaseCredits: config.draft.minPurchaseCredits,
        apis: config.draft.apis,
        operations: config.draft.operations,
        note: 'Updated via super-admin dashboard',
      };
      const updated = await updatePricing(body);
      setConfig({ original: updated, draft: JSON.parse(JSON.stringify(updated)) });
      setSaved(true);
      setShowSaveConfirm(false);
      setTimeout(() => setSaved(false), 3000);
    } catch (err: any) {
      setSaveError(err?.message || 'Save failed');
      setShowSaveConfirm(false);
    } finally {
      setSaving(false);
    }
  };

  const handleApplyPending = async (sk: string) => {
    setPendingBusySk(sk);
    setPendingError(null);
    try {
      await applyPendingChange(sk);
      await loadPending();
      await loadConfig();
    } catch (err: any) {
      setPendingError(err?.message || 'Apply failed');
    } finally {
      setPendingBusySk(null);
    }
  };

  const handleDismissPending = async (sk: string) => {
    setPendingBusySk(sk);
    setPendingError(null);
    try {
      await dismissPendingChange(sk);
      await loadPending();
    } catch (err: any) {
      setPendingError(err?.message || 'Dismiss failed');
    } finally {
      setPendingBusySk(null);
    }
  };

  const handleForceResync = async (api: 'anthropic' | 'google_vertex' | 'google_cse') => {
    setResyncBusy(api);
    setResyncError(null);
    try {
      await forceResync(api);
      await loadConfig();
    } catch (err: any) {
      setResyncError(`${api}: ${err?.message || 'Resync failed'}`);
      // Backend may not be live (Track D) — mark endpoint unavailable
      if (err?.message?.includes('404')) {
        setResyncAvailable((r) => ({ ...r, [api]: false }));
      }
    } finally {
      setResyncBusy(null);
    }
  };

  if (loading) {
    return (
      <RubyLoader variant="section" label="Loading pricing config…" className="py-10" />
    );
  }
  if (error) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 flex items-start gap-2 text-sm text-rose-700">
        <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold">Failed to load pricing config</p>
          <p className="text-xs text-rose-600 mt-0.5">{error}</p>
          <button
            type="button"
            onClick={loadConfig}
            className="mt-2 text-xs font-semibold underline text-rose-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
  if (!config.draft) return null;

  const d = config.draft;

  return (
    <div className="space-y-5">
      {/* Pending Pricing Changes */}
      {pendingError && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-800 flex items-start gap-2">
          <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
          Failed to load pending changes: {pendingError}
        </div>
      )}
      {pending.length > 0 && (
        <section className="bg-white border border-amber-300 rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-amber-200 bg-amber-50 flex items-center gap-2">
            <Bell size={14} className="text-amber-600" />
            <h2 className="text-sm font-semibold text-amber-900">
              Pending Pricing Changes ({pending.length})
            </h2>
          </div>
          <ul className="divide-y divide-stone-100">
            {pending.map((p) => (
              <li key={p.sk} className="p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-stone-900">
                      {p.api} · <span className="font-mono text-xs text-stone-600">{p.field}</span>
                    </div>
                    <div className="text-xs text-stone-500 mt-0.5">
                      Detected {new Date(p.detectedAt).toLocaleString()}
                    </div>
                    <div className="text-xs text-stone-700 mt-1">
                      Current:{' '}
                      <span className="font-semibold">{p.currentValue}</span>
                      {' → '}
                      Detected:{' '}
                      <span className="font-semibold text-amber-700">{p.detectedValue}</span>
                      {'  '}
                      <span
                        className={`text-[11px] font-semibold ${p.deltaPercent >= 0 ? 'text-rose-600' : 'text-emerald-600'}`}
                      >
                        ({p.deltaPercent >= 0 ? '+' : ''}
                        {p.deltaPercent.toFixed(1)}%)
                      </span>
                    </div>
                    {p.metadata?.expectedUsd !== undefined && (
                      <div className="text-[11px] text-stone-500 mt-0.5">
                        Expected ${p.metadata.expectedUsd.toFixed(4)} · Actual ${p.metadata.actualUsd?.toFixed(4)}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1.5 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => handleApplyPending(p.sk)}
                      disabled={pendingBusySk === p.sk}
                      className="px-3 py-2 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 min-h-[36px] inline-flex items-center gap-1"
                    >
                      {pendingBusySk === p.sk ? (
                        <RubyGemMark size={10} drawing color="currentColor" />
                      ) : (
                        <Check size={10} />
                      )}
                      Apply
                    </button>
                    <button
                      type="button"
                      onClick={() => setApplyAdjustSk(p.sk)}
                      disabled={pendingBusySk === p.sk}
                      className="px-3 py-2 rounded-lg text-xs font-semibold bg-white border border-stone-300 text-stone-700 hover:bg-stone-50 disabled:opacity-50 min-h-[36px]"
                    >
                      Apply + adjust
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDismissPending(p.sk)}
                      disabled={pendingBusySk === p.sk}
                      className="px-3 py-2 rounded-lg text-xs font-semibold bg-stone-100 text-stone-700 hover:bg-stone-200 disabled:opacity-50 min-h-[36px] inline-flex items-center gap-1"
                    >
                      <X size={10} />
                      Dismiss
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Global credit unit price + min purchase */}
      <section className="bg-white border border-stone-200 rounded-xl shadow-sm p-4">
        <div className="flex items-center gap-2 mb-3">
          <DollarSign size={14} className="text-stone-500" />
          <h2 className="text-sm font-semibold text-stone-900">Global</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-xs text-stone-500">Credit unit price (USD)</span>
            <div className="mt-1 flex items-center gap-2">
              <span className="text-stone-400">$</span>
              <input
                type="number"
                step={0.0001}
                min={0}
                value={d.creditUnitPriceUsd}
                onChange={(e) =>
                  setDraft((x) => {
                    x.creditUnitPriceUsd = parseFloat(e.target.value) || 0;
                  })
                }
                className="flex-1 px-3 py-2.5 bg-stone-50 border border-stone-200 rounded-xl text-stone-800 text-sm min-h-[44px]"
              />
              <span className="text-xs text-stone-500">per credit</span>
            </div>
          </label>
          <label className="block">
            <span className="text-xs text-stone-500">Minimum purchase (global)</span>
            <div className="mt-1 flex items-center gap-2">
              <input
                type="number"
                min={1}
                step={1}
                value={d.minPurchaseCredits}
                onChange={(e) =>
                  setDraft((x) => {
                    x.minPurchaseCredits = parseInt(e.target.value, 10) || 0;
                  })
                }
                className="flex-1 px-3 py-2.5 bg-stone-50 border border-stone-200 rounded-xl text-stone-800 text-sm min-h-[44px]"
              />
              <span className="text-xs text-stone-500">credits</span>
            </div>
          </label>
        </div>
      </section>

      {/* Per-API cards */}
      <section className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {(['anthropic', 'google_vertex', 'google_cse'] as const).map((apiKey) => {
          const api = d.apis[apiKey];
          if (!api) {
            return (
              <div
                key={apiKey}
                className="bg-stone-50 border border-dashed border-stone-300 rounded-xl p-4 text-xs text-stone-500"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Info size={12} />
                  <span className="font-semibold">{apiKey}</span>
                </div>
                Not configured in global pricing yet.
              </div>
            );
          }
          return (
            <ApiCard
              key={apiKey}
              apiKey={apiKey}
              api={api}
              onUpdate={(updater) =>
                setDraft((x) => {
                  updater(x.apis[apiKey]!);
                })
              }
              onResync={() => handleForceResync(apiKey)}
              resyncAvailable={resyncAvailable[apiKey]}
              resyncBusy={resyncBusy === apiKey}
            />
          );
        })}
      </section>

      {resyncError && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-800 flex items-start gap-2">
          <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold">Force resync failed</p>
            <p className="mt-0.5">{resyncError}</p>
            <p className="mt-0.5 text-[10px] text-amber-600">
              Note: Force resync endpoint is owned by Wave 1 Track D (cron pricing sync).
              If it's not yet deployed, this will 404 until Track D finishes.
            </p>
          </div>
        </div>
      )}

      {/* Credit Bundles */}
      <section className="bg-white border border-stone-200 rounded-xl shadow-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-stone-900">Credit Bundles</h2>
          <a
            href="https://dashboard.stripe.com/products?active=true"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-rose-600 hover:text-rose-700 min-h-[32px] px-2 py-1"
          >
            Open Stripe Dashboard
            <ExternalLink size={12} />
          </a>
        </div>
        <p className="text-[11px] text-stone-500 mb-3">
          Bundles are managed in Stripe. Products must have metadata:{' '}
          <code className="text-[10px] bg-stone-100 px-1 rounded">credit_product=true</code>,{' '}
          <code className="text-[10px] bg-stone-100 px-1 rounded">credits_per_unit=N</code>
        </p>
        {bundlesError ? (
          <div className="text-xs text-stone-500 bg-stone-50 border border-stone-200 rounded-lg p-3">
            Bundles not available — create them in Stripe first. ({bundlesError})
          </div>
        ) : bundles === null ? (
          <div className="p-2"><RubyLoader variant="inline" size={14} label="Loading bundles…" /></div>
        ) : bundles.length === 0 ? (
          <div className="text-xs text-stone-500 p-2">No credit bundles configured.</div>
        ) : (
          <ul className="divide-y divide-stone-100 border border-stone-200 rounded-lg">
            {bundles.map((b) => (
              <li key={b.id} className="px-3 py-2 flex items-center justify-between text-xs">
                <div>
                  <span className="font-semibold text-stone-800">{b.label}</span>
                  <span className="text-stone-500 ml-2">
                    {b.creditsPerUnit} credits / ${(b.unitPriceUsd ?? 0).toFixed(2)}
                    {b.currency ? ` ${b.currency}` : ''}
                  </span>
                </div>
                <span className="text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wide bg-emerald-100 text-emerald-700">
                  Active
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Change History */}
      <section className="bg-white border border-stone-200 rounded-xl shadow-sm p-4">
        <div className="flex items-center gap-2 mb-3">
          <History size={14} className="text-stone-500" />
          <h2 className="text-sm font-semibold text-stone-900">Change History</h2>
        </div>
        {!d.changeHistory || d.changeHistory.length === 0 ? (
          <div className="text-xs text-stone-500">No changes recorded yet.</div>
        ) : (
          <ul className="divide-y divide-stone-100">
            {d.changeHistory.slice(0, 20).map((c, i) => (
              <li key={i} className="py-2 text-xs">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-stone-400 font-mono text-[11px]">
                    {new Date(c.at).toLocaleString()}
                  </span>
                  <span className="text-stone-500">{c.by}</span>
                </div>
                <div className="text-stone-700 mt-0.5">
                  <span className="font-mono text-[11px]">{c.diff.field}</span>
                  {': '}
                  <span className="text-stone-500">{String(c.diff.before)}</span>
                  {' → '}
                  <span className="font-semibold">{String(c.diff.after)}</span>
                </div>
                {c.note && <div className="text-stone-400 text-[11px] mt-0.5">{c.note}</div>}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Save bar */}
      <div className="sticky bottom-0 bg-white border border-stone-200 rounded-xl shadow-lg px-4 py-3 flex items-center justify-between">
        <div className="text-xs text-stone-500 flex items-center gap-2">
          {saved ? (
            <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
              <Check size={12} /> Saved
            </span>
          ) : isDirty ? (
            <span className="inline-flex items-center gap-1 text-amber-700">
              <Clock size={12} /> Unsaved changes
            </span>
          ) : (
            <span>No pending changes</span>
          )}
          {saveError && <span className="text-rose-600">· {saveError}</span>}
        </div>
        <button
          type="button"
          onClick={handleSaveClick}
          disabled={!isDirty || saving}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 min-h-[44px]"
        >
          <Save size={14} />
          Save &amp; Publish
        </button>
      </div>

      <ConfirmModal
        open={showSaveConfirm}
        title="Save pricing changes?"
        message={
          <>
            These changes will take effect immediately for all tenants on the default burn rate.
            Tenants with per-tenant overrides keep their overrides.
          </>
        }
        confirmLabel="Save & Publish"
        cancelLabel="Cancel"
        onConfirm={handleConfirmSave}
        onCancel={() => setShowSaveConfirm(false)}
        tone="emerald"
        busy={saving}
      />

      {applyAdjustSk && (
        <ApplyAdjustBurnModal
          change={pending.find((p) => p.sk === applyAdjustSk) || null}
          onClose={() => setApplyAdjustSk(null)}
          onApplied={async () => {
            setApplyAdjustSk(null);
            await loadPending();
            await loadConfig();
          }}
        />
      )}
    </div>
  );
};

interface ApiCardProps {
  apiKey: 'anthropic' | 'google_vertex' | 'google_cse';
  api: NonNullable<AgentPricingConfig['apis']['anthropic']>;
  onUpdate: (updater: (a: NonNullable<AgentPricingConfig['apis']['anthropic']>) => void) => void;
  onResync: () => void;
  resyncAvailable: boolean;
  resyncBusy: boolean;
}

const ApiCard: React.FC<ApiCardProps> = ({ apiKey, api, onUpdate, onResync, resyncAvailable, resyncBusy }) => {
  const labelMap: Record<string, string> = {
    anthropic: 'Anthropic Claude',
    google_vertex: 'Google Vertex AI',
    google_cse: 'AI Grounding',
  };

  return (
    <div className="bg-white border border-stone-200 rounded-xl shadow-sm p-4 flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-stone-900">{api.label || labelMap[apiKey]}</h3>
          <p className="text-[11px] text-stone-500 font-mono">{apiKey}</p>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={api.enabled}
            onChange={(e) => onUpdate((a) => { a.enabled = e.target.checked; })}
            className="sr-only peer"
          />
          <span className="w-9 h-5 bg-stone-300 peer-checked:bg-emerald-500 rounded-full relative transition-colors">
            <span className="absolute top-0.5 left-0.5 h-4 w-4 bg-white rounded-full transition-transform peer-checked:translate-x-4" />
          </span>
          <span className="text-[11px] text-stone-600">{api.enabled ? 'Enabled' : 'Disabled'}</span>
        </label>
      </div>

      {/* Burn rate slider */}
      <label className="block mb-3">
        <div className="flex items-center justify-between">
          <span className="text-xs text-stone-500">Burn rate</span>
          <span className="text-sm font-semibold text-stone-900">{api.burnRate.toFixed(1)}×</span>
        </div>
        <input
          type="range"
          min={1}
          max={20}
          step={0.1}
          value={api.burnRate}
          onChange={(e) => onUpdate((a) => { a.burnRate = parseFloat(e.target.value); })}
          className="w-full mt-1"
        />
      </label>

      {/* Pricing fields */}
      <div className="space-y-2 mb-3">
        {Object.entries(api.pricing || {}).map(([field, val]) => (
          <label key={field} className="block">
            <span className="text-[11px] text-stone-500 font-mono">{field}</span>
            <div className="mt-0.5 flex items-center gap-1.5">
              <span className="text-stone-400 text-xs">$</span>
              <input
                type="number"
                step={0.0001}
                min={0}
                value={val}
                onChange={(e) =>
                  onUpdate((a) => {
                    a.pricing[field] = parseFloat(e.target.value) || 0;
                  })
                }
                className="flex-1 px-2 py-1.5 bg-stone-50 border border-stone-200 rounded-lg text-stone-800 text-xs min-h-[36px]"
              />
            </div>
          </label>
        ))}
      </div>

      <label className="block mb-3">
        <span className="text-[11px] text-stone-500">Min credits per call</span>
        <input
          type="number"
          min={0}
          step={1}
          value={api.minCreditsPerCall}
          onChange={(e) => onUpdate((a) => { a.minCreditsPerCall = parseInt(e.target.value, 10) || 0; })}
          className="mt-0.5 w-full px-2 py-1.5 bg-stone-50 border border-stone-200 rounded-lg text-stone-800 text-xs min-h-[36px]"
        />
      </label>

      <div className="mt-auto flex items-center justify-between text-[11px] text-stone-500 pt-2 border-t border-stone-100">
        <span>
          {api.lastSyncedAt
            ? `Last synced ${new Date(api.lastSyncedAt).toLocaleString()}`
            : api.lastVerifiedAt
              ? `Last verified ${new Date(api.lastVerifiedAt).toLocaleString()}`
              : 'Never synced'}
        </span>
        <button
          type="button"
          onClick={onResync}
          disabled={!resyncAvailable || resyncBusy}
          title={resyncAvailable ? 'Force resync pricing from upstream' : 'Force resync endpoint not yet available (Wave 1 Track D)'}
          className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium bg-stone-100 text-stone-700 hover:bg-stone-200 disabled:opacity-40 min-h-[28px]"
        >
          {resyncBusy ? <RubyGemMark size={10} drawing color="currentColor" /> : <RefreshCw size={10} />}
          Resync
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// ApplyAdjustBurnModal — for pending changes, applies the new value AND
// tweaks burn rate in the same transaction.
// ---------------------------------------------------------------------------
const ApplyAdjustBurnModal: React.FC<{
  change: AgentPricingPendingChange | null;
  onClose: () => void;
  onApplied: () => void;
}> = ({ change, onClose, onApplied }) => {
  const [burnRate, setBurnRate] = useState<string>('5.0');
  const [note, setNote] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!change) return null;

  const handleApply = async () => {
    const b = parseFloat(burnRate);
    if (!(b >= 0 && b <= 50)) {
      setError('Burn rate must be 0–50');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await applyPendingChange(change.sk, {
        actionNote: note.trim() || undefined,
        adjustBurnRate: b,
      });
      onApplied();
    } catch (err: any) {
      setError(err?.message || 'Apply failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[1000] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl border border-stone-200 shadow-xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="w-11 h-11 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <AlertTriangle size={22} className="text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-stone-900">Apply + adjust burn</h3>
            <p className="text-xs text-stone-500">
              {change.api} · <span className="font-mono">{change.field}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="text-stone-400 hover:text-stone-600 p-1 min-w-[32px] min-h-[32px]"
          >
            <X size={18} />
          </button>
        </div>
        <p className="text-xs text-stone-600 mb-3">
          Apply detected value{' '}
          <span className="font-semibold">{change.detectedValue}</span> AND adjust {change.api}{' '}
          burn rate to hold the credit price steady.
        </p>
        <label className="block mb-3">
          <span className="text-xs font-medium text-stone-700">New burn rate (0–50)</span>
          <input
            type="number"
            min={0}
            max={50}
            step={0.1}
            value={burnRate}
            onChange={(e) => setBurnRate(e.target.value)}
            disabled={busy}
            className="mt-1 w-full px-3 py-2.5 bg-stone-50 border border-stone-200 rounded-xl text-stone-800 text-sm min-h-[44px]"
          />
        </label>
        <label className="block mb-3">
          <span className="text-xs font-medium text-stone-700">Note (optional)</span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={busy}
            rows={2}
            placeholder="Q2 repricing"
            className="mt-1 w-full px-3 py-2 bg-stone-50 border border-stone-200 rounded-xl text-stone-800 text-sm resize-none"
          />
        </label>
        {error && (
          <div className="mb-3 bg-rose-50 border border-rose-200 text-rose-700 text-xs rounded-lg px-3 py-2">
            {error}
          </div>
        )}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="flex-1 px-4 py-3 bg-stone-100 text-stone-700 font-semibold rounded-xl min-h-[44px]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleApply}
            disabled={busy}
            className="flex-1 px-4 py-3 bg-amber-600 text-white font-bold rounded-xl hover:bg-amber-700 disabled:opacity-50 min-h-[44px] inline-flex items-center justify-center gap-2"
          >
            {busy ? <RubyGemMark size={14} drawing color="currentColor" /> : null}
            Apply + adjust
          </button>
        </div>
      </div>
    </div>
  );
};

export default PricingTab;
